import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "motion/react";
import {
  Search,
  Eye,
  CheckCircle2,
  ScanFace,
  Users,
  Clock,
  CreditCard,
  X,
  ArrowUpDown,
  ChefHat as ChefHatLucide,
  MapPin,
  AlertCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChefDetailModal } from "@/routes/dashboard/admin/-components/ChefDetailModal";
import { chefQueries } from "@/domain/chef/chef.query";
import { authMutations } from "@/domain/auth/auth.query";
import { getIpxImageUrl } from "@/lib/image";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/admin/chefs")({
  loader: ({ context }) => {
    return context.queryClient.ensureQueryData(chefQueries.adminChefs());
  },
  component: RouteComponent,
});

type Tab = "all" | "pending" | "verified";
type Sort = "recent" | "alpha";

function RouteComponent() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [tab, setTab] = useState<Tab>("pending");
  const [sort, setSort] = useState<Sort>("recent");
  const [selectedChef, setSelectedChef] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: chefs = [], isLoading } = useQuery(chefQueries.adminChefs());
  const impersonateMutation = useMutation(authMutations.impersonateChef());

  const impersonateChef = async (chefId: number) => {
    await impersonateMutation.mutateAsync({ chefId });
    navigate({ to: "/dashboard" });
  };

  // Stats globales
  const stats = useMemo(() => {
    const total = chefs.length;
    const verified = chefs.filter((c: any) => c.accountVerified).length;
    const pending = total - verified;
    const noStripe = chefs.filter((c: any) => !c.stripeAccountId).length;
    return { total, verified, pending, noStripe };
  }, [chefs]);

  // Filtre + tri
  const filteredChefs = useMemo(() => {
    const matching = (chefs as any[]).filter((chef) => {
      const matchesSearch =
        searchTerm === "" ||
        `${chef.firstName} ${chef.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        chef.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chef.workingCity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chef.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (chef.specialisation &&
          chef.specialisation.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesTab =
        tab === "all" ||
        (tab === "pending" && !chef.accountVerified) ||
        (tab === "verified" && chef.accountVerified);

      return matchesSearch && matchesTab;
    });

    return matching.sort((a, b) => {
      if (sort === "alpha") {
        return `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`,
        );
      }
      return (
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    });
  }, [chefs, searchTerm, tab, sort]);

  const handleViewChef = (chef: any) => {
    setSelectedChef(chef);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedChef(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* ─────────  Header  ───────── */}
      <header>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">
            Espace admin
          </span>
          <span className="w-1 h-1 rounded-full bg-primary" />
          <span className="text-[11px] text-muted-foreground">Chefs</span>
        </div>
        <h1 className="font-h1 text-dark-blue">Gestion des chefs</h1>
        <p className="font-small text-muted-foreground mt-1.5 max-w-xl">
          Validez les nouvelles candidatures, suspendez les profils inactifs et
          gardez un œil sur la communauté HomeToq.
        </p>
      </header>

      {/* ─────────  KPI tiles  ───────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPITile
          label="Total chefs"
          value={stats.total}
          icon={Users}
          tone="neutral"
        />
        <KPITile
          label="En attente"
          value={stats.pending}
          icon={Clock}
          tone="warn"
          highlight={stats.pending > 0}
          onClick={() => setTab("pending")}
        />
        <KPITile
          label="Vérifiés"
          value={stats.verified}
          icon={CheckCircle2}
          tone="success"
          onClick={() => setTab("verified")}
        />
        <KPITile
          label="Sans Stripe"
          value={stats.noStripe}
          icon={CreditCard}
          tone="danger"
        />
      </div>

      {/* ─────────  Tabs  ───────── */}
      <div className="flex flex-wrap gap-2 border-b border-border">
        <TabButton
          label="En attente"
          count={stats.pending}
          active={tab === "pending"}
          onClick={() => setTab("pending")}
          urgent={stats.pending > 0}
        />
        <TabButton
          label="Vérifiés"
          count={stats.verified}
          active={tab === "verified"}
          onClick={() => setTab("verified")}
        />
        <TabButton
          label="Tous"
          count={stats.total}
          active={tab === "all"}
          onClick={() => setTab("all")}
        />
      </div>

      {/* ─────────  Toolbar (search + sort)  ───────── */}
      <div className="bg-white rounded-2xl border border-border p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 flex-1 min-w-[200px]">
          <Search size={16} className="text-muted-foreground shrink-0" />
          <Input
            placeholder="Rechercher par nom, ville, email, spécialisation..."
            className="border-none shadow-none p-0 h-9 focus-visible:ring-0"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="text-muted-foreground hover:text-dark-blue transition-colors"
              aria-label="Effacer"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="h-8 w-px bg-border" />

        <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
          <SelectTrigger className="w-[180px] h-9 border-none shadow-none focus:ring-0">
            <ArrowUpDown size={14} className="text-muted-foreground mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Plus récents d'abord</SelectItem>
            <SelectItem value="alpha">Ordre alphabétique</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ─────────  Cards  ───────── */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-[260px] rounded-2xl bg-white border border-border animate-pulse"
            />
          ))}
        </div>
      ) : filteredChefs.length === 0 ? (
        <EmptyState searchTerm={searchTerm} tab={tab} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredChefs.map((chef, i) => (
            <ChefAdminCard
              key={chef.id}
              chef={chef}
              index={i}
              onView={() => handleViewChef(chef)}
              onImpersonate={() => impersonateChef(chef.id)}
            />
          ))}
        </div>
      )}

      {selectedChef && (
        <ChefDetailModal
          chef={selectedChef}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────────

function KPITile({
  label,
  value,
  icon: Icon,
  tone,
  highlight = false,
  onClick,
}: {
  label: string;
  value: number;
  icon: typeof Users;
  tone: "neutral" | "warn" | "success" | "danger";
  highlight?: boolean;
  onClick?: () => void;
}) {
  const toneClasses = {
    neutral: "bg-cream/50 text-dark-blue",
    warn: "bg-yellow-100 text-yellow-700",
    success: "bg-emerald-100 text-emerald-700",
    danger: "bg-red-100 text-red-700",
  } as const;

  const Component = onClick ? "button" : "div";

  return (
    <Component
      onClick={onClick}
      className={cn(
        "bg-white rounded-2xl border p-4 text-left transition-all",
        highlight
          ? "border-primary/30 shadow-[0_4px_12px_-2px_rgba(232,89,60,0.15)]"
          : "border-border",
        onClick && "hover:shadow-md hover:border-primary/30 cursor-pointer",
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center",
            toneClasses[tone],
          )}
        >
          <Icon size={16} />
        </span>
        {highlight && (
          <span className="text-[9px] font-bold uppercase tracking-widest text-primary inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            Action requise
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-dark-blue tabular-nums leading-none">
        {value}
      </p>
      <p className="font-small text-muted-foreground mt-1">{label}</p>
    </Component>
  );
}

function TabButton({
  label,
  count,
  active,
  onClick,
  urgent = false,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  urgent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex items-center gap-2 px-4 py-2.5 font-button-small transition-colors -mb-px",
        active
          ? "text-primary border-b-2 border-primary"
          : "text-muted-foreground border-b-2 border-transparent hover:text-dark-blue",
      )}
    >
      {label}
      {count > 0 && (
        <span
          className={cn(
            "min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center tabular-nums",
            active
              ? "bg-primary text-white"
              : urgent
                ? "bg-primary/15 text-primary"
                : "bg-cream text-muted-foreground",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ChefAdminCard({
  chef,
  index,
  onView,
  onImpersonate,
}: {
  chef: any;
  index: number;
  onView: () => void;
  onImpersonate: () => void;
}) {
  const isPending = !chef.accountVerified;
  const hasStripe = !!chef.stripeAccountId;
  const createdAt = chef.createdAt ? new Date(chef.createdAt) : null;
  const ageLabel = createdAt
    ? formatDistanceToNow(createdAt, { addSuffix: true, locale: fr })
    : null;

  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.2) }}
      className={cn(
        "bg-white rounded-2xl border overflow-hidden flex flex-col transition-all hover:shadow-md",
        isPending
          ? "border-primary/30 hover:border-primary/50"
          : "border-border hover:border-primary/20",
      )}
    >
      {/* Status banner — uniquement pour les pending */}
      {isPending && (
        <div className="bg-primary/8 px-4 py-1.5 flex items-center gap-1.5 border-b border-primary/15">
          <AlertCircle size={11} className="text-primary" />
          <p className="font-button-extra-small uppercase tracking-wider text-primary">
            Candidature à valider
          </p>
        </div>
      )}

      <div className="p-5 flex flex-col gap-4 flex-1">
        {/* Header : avatar + name + status icon */}
        <div className="flex items-start gap-3">
          <Avatar className="h-14 w-14 border-2 border-white shadow-sm">
            <AvatarImage
              src={
                chef.avatar
                  ? getIpxImageUrl(chef.avatar, 150)
                  : "/ipx/f_webp,q_75,w_150/images/find_chef_avatar.webp"
              }
              alt={`${chef.firstName} ${chef.lastName}`}
            />
            <AvatarFallback className="bg-primary/10 text-primary font-bold">
              {chef.firstName?.[0]}
              {chef.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-h4 text-dark-blue leading-tight truncate">
              {chef.firstName} {chef.lastName}
            </p>
            <p className="font-small text-muted-foreground truncate mt-0.5">
              {chef.specialisation || "Spécialisation non renseignée"}
            </p>
            {ageLabel && (
              <p className="font-button-extra-small uppercase tracking-wider text-muted-foreground/70 mt-1">
                Inscrit {ageLabel}
              </p>
            )}
          </div>
        </div>

        {/* Mini infos */}
        <div className="space-y-2">
          {chef.workingCity && (
            <div className="flex items-center gap-2 font-small text-dark-blue">
              <MapPin size={12} className="text-primary/70 shrink-0" />
              <span className="truncate">
                {chef.workingCity}
                {chef.city && chef.city !== chef.workingCity && (
                  <span className="text-muted-foreground">
                    {" "}
                    · {chef.city}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Tags status */}
        <div className="flex flex-wrap gap-1.5">
          <span
            className={cn(
              "inline-flex items-center gap-1 font-button-extra-small uppercase tracking-wider px-2 py-1 rounded-md",
              isPending
                ? "bg-yellow-100 text-yellow-800"
                : "bg-emerald-100 text-emerald-800",
            )}
          >
            {isPending ? (
              <>
                <Clock size={10} />
                En attente
              </>
            ) : (
              <>
                <CheckCircle2 size={10} />
                Vérifié
              </>
            )}
          </span>
          <span
            className={cn(
              "inline-flex items-center gap-1 font-button-extra-small uppercase tracking-wider px-2 py-1 rounded-md",
              hasStripe
                ? "bg-emerald-100 text-emerald-800"
                : "bg-red-100 text-red-800",
            )}
          >
            <CreditCard size={10} />
            Stripe {hasStripe ? "OK" : "manquant"}
          </span>
        </div>

        {/* Actions */}
        <div className="mt-auto flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onView}
            className={cn(
              "flex-1 inline-flex items-center justify-center gap-1.5 font-button-small px-3 py-2.5 rounded-xl transition-colors",
              isPending
                ? "bg-primary text-white hover:bg-primary/90"
                : "bg-dark-blue text-white hover:bg-primary",
            )}
          >
            <Eye size={14} />
            {isPending ? "Examiner" : "Voir détails"}
          </button>
          <button
            type="button"
            onClick={onImpersonate}
            title="Se connecter en tant que ce chef"
            className="w-10 h-10 rounded-xl bg-cream hover:bg-cream/70 text-dark-blue flex items-center justify-center transition-colors border border-border"
          >
            <ScanFace size={15} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}

function EmptyState({
  searchTerm,
  tab,
}: {
  searchTerm: string;
  tab: Tab;
}) {
  if (searchTerm) {
    return (
      <div className="bg-white rounded-2xl border-2 border-dashed border-border p-12 text-center">
        <Search className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <h3 className="font-button-medium text-dark-blue">
          Aucun chef ne correspond à "{searchTerm}"
        </h3>
        <p className="font-small text-muted-foreground mt-1">
          Essayez un autre nom, ville ou spécialisation.
        </p>
      </div>
    );
  }

  if (tab === "pending") {
    return (
      <div className="bg-white rounded-2xl border-2 border-dashed border-border p-12 text-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
        <h3 className="font-button-medium text-dark-blue">
          Aucune candidature en attente
        </h3>
        <p className="font-small text-muted-foreground mt-1">
          Vous êtes à jour. Tous les chefs inscrits ont été validés.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border-2 border-dashed border-border p-12 text-center">
      <ChefHatLucide className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
      <h3 className="font-button-medium text-dark-blue">Aucun chef</h3>
      <p className="font-small text-muted-foreground mt-1">
        Les nouveaux inscrits apparaîtront ici.
      </p>
    </div>
  );
}
